import React from 'react';
import type { ComponentPropsWithoutRef } from 'react';
import type { LinkProps } from 'react-router-dom';

import PropTypes from 'prop-types';

import { Icon } from 'apps/auth/components/common/Icon/Icon';
import { Text } from 'apps/auth/components/common/Text/Text';

import { ButtonIcon, StackButtonLink, StackButtonReactLink } from './styles';

interface BaseProps {
    buttonIcon?: string;
    buttonCaption?: string;
    isDisabled?: boolean;
    onClick?: ComponentPropsWithoutRef<'a'>['onClick'];
}

interface AnchorStackButtonProps extends BaseProps {
    type?: 'link';
    href?: string;
    to?: never;
}

interface ReactLinkStackButtonProps extends BaseProps {
    type: 'reactLink';
    to: LinkProps['to'];
    href?: never;
}

export type StackButtonProps = AnchorStackButtonProps | ReactLinkStackButtonProps;

type StackButtonColor = 'primary' | 'lightGray';

const getColor = (isDisabled?: boolean): StackButtonColor => (isDisabled ? 'lightGray' : 'primary');

const StackButtonComponent = ({ type = 'link', buttonIcon, buttonCaption, isDisabled, onClick, href, to }: StackButtonProps) => {
    const color = getColor(isDisabled);

    if (type === 'reactLink') {
        return (
            <StackButtonReactLink to={to ?? '#'} $isDisabled={isDisabled} onClick={onClick}>
                <ButtonIcon $isDisabled={isDisabled}>
                    <Icon name={buttonIcon ?? ''} color={color} />
                </ButtonIcon>
                <Text size="secondary" color={color} ellipsis>
                    {buttonCaption}
                </Text>
            </StackButtonReactLink>
        );
    }

    return (
        <StackButtonLink href={href} $isDisabled={isDisabled} onClick={onClick}>
            <ButtonIcon $isDisabled={isDisabled}>
                <Icon name={buttonIcon ?? ''} color={color} />
            </ButtonIcon>
            <Text size="secondary" color={color} ellipsis>
                {buttonCaption}
            </Text>
        </StackButtonLink>
    );
};

StackButtonComponent.propTypes = {
    type: PropTypes.oneOf(['link', 'reactLink']),
    buttonIcon: PropTypes.string,
    buttonCaption: PropTypes.string,
    isDisabled: PropTypes.bool,
    href: PropTypes.string,
    to: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    onClick: PropTypes.func,
};

StackButtonComponent.displayName = 'StackButton';

export const StackButton = React.memo(StackButtonComponent);
